// Library methods
import { useEffect, useMemo } from "react";

// MUI Components
import { Grid } from "@mui/material";
import ColorVisionPdfReport from "../../containers/Report/ColorVisionReport/EyeReport";
import ContrastPdfReport from "../../containers/Report/ContrastSensitivityReport/EyeReport";
import SynoptophoreReport from "../../containers/Report/SynoptophoreReport/EyeReport";
import EstermanPdfReport from "../../containers/Report/EstermanReport/EyeReport";
import VisualFieldPdfReport from "../../containers/Report/VisualFieldReport/EyeReport";
import VisualAcuityPdfReport from "../../containers/Report/VisualAcuityReport/EyeReport";
import Full120Report from "../../containers/Report/SingleThresholdReport/EyeReport";
import ReportPdf from "../../components/UI/Report";
import MultiExamReportPanel from "../../containers/Report/ReportPanel/MultiExamReportPanel";
import { ErrorMessageComponent } from "../../components/UI/ErrorMessage";

// Utilities
import { ReportEachSectionContext } from "../../contexts/CommentContext";
import { ReportDataContext } from "../../contexts/ReportDataContext";
import useClinic from "../../hooks/useClinic";
import { getClinics } from "../../services/Clinic";
import { examCardEnums } from "../../enums";

const RegularExamPDF = ({ clinicId, patientId, examId }) => {
  const forBackendPdf = true;
  const reportPdfValues = ReportPdf(forBackendPdf, clinicId, patientId, examId);
  const { exam, reportData, shouldDisplayReport } = reportPdfValues;
  const { clinicSettings, setClinicLicensing } = useClinic();

  useEffect(() => {
    try {
      if (clinicId) {
        getClinics(null, clinicId).then((res) => {
          const { licensing } = res;
          if (licensing) setClinicLicensing(licensing);
        });
      }
    } catch (e) {
      // console.log(e);
    }
  }, [clinicId, setClinicLicensing]);

  const SectionWrapper = ({
    report,
    sectionId,
    childComponent,
    eyeReport,
    durationStartTime,
    duration,
    comments,
    needBreak = null,
  }) => {
    return report ? (
      <Grid
        item
        xs={12}
        lg={6}
        sx={{
          pageBreakAfter: needBreak ? "always" : "auto",
        }}
      >
        <ReportEachSectionContext.Provider
          value={{
            sectionId,
            examReport: report,
            eyeReport,
            durationStartTime,
            duration,
            comments,
          }}
        >
          {childComponent}
        </ReportEachSectionContext.Provider>
      </Grid>
    ) : (
      <></>
    );
  };

  const currentExamSections = useMemo(() => {
    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );

    return examSectionsKeys.filter(
      (key) =>
        exam[key]?.length > 0 &&
        exam[key]?.some((examSection) => Boolean(examSection.completionDate))
    );
  }, [exam]);

  const isMultiExamPackage = useMemo(() => {
    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );

    return examSectionsKeys.filter((key) => exam[key]?.length > 0)?.length > 1;
  }, [exam]);

  const reportSection = useMemo(() => {
    // add section classNames to multi-exam packages only
    const sectionClassName = isMultiExamPackage
      ? "retinalogik-package"
      : undefined;

    return (
      <>
        {currentExamSections.map((sectionName, index) => {
          if (!reportData?.[sectionName]?.display) {
            return <div key={index}></div>;
          }

          const {
            examReport,
            examReport2,
            sectionId,
            sectionId2,
            eyeReport,
            eyeReport2,
            duration,
            duration2,
            comments,
            comments2,
            durationStartTime,
            durationStartTime2,
          } = reportData?.[sectionName] ?? {};

          const needBreak =
            exam?.[sectionName]?.[0]?.completionDate &&
            exam?.[sectionName]?.[1]?.completionDate;

          const displayCanvas =
            clinicSettings?.monocularReportPdfLayoutOption === "Single" &&
            sectionId != null &&
            sectionId2 != null;

          switch (sectionName) {
            case examCardEnums.COLOR_VISION.sectionName:
              return (
                <div key={`${sectionName}-${index}`} style={{ width: "100%" }}>
                  <Grid
                    container
                    className={sectionClassName}
                    columnSpacing={4}
                  >
                    <SectionWrapper
                      report={examReport}
                      sectionId={sectionId}
                      childComponent={<ColorVisionPdfReport />}
                      eyeReport={eyeReport}
                      duration={duration}
                      comments={comments}
                      needBreak={needBreak}
                    />
                    <SectionWrapper
                      report={examReport2}
                      sectionId={sectionId2}
                      childComponent={<ColorVisionPdfReport />}
                      eyeReport={eyeReport2}
                      duration={duration2}
                      comments={comments2}
                    />
                  </Grid>
                  {displayCanvas && <canvas width={1920}></canvas>}
                </div>
              );
            case examCardEnums.CONTRAST_SENSITIVITY.sectionName:
              return (
                <div key={`${sectionName}-${index}`} style={{ width: "100%" }}>
                  <Grid
                    container
                    className={sectionClassName}
                    columnSpacing={4}
                  >
                    <SectionWrapper
                      report={examReport}
                      sectionId={sectionId}
                      childComponent={<ContrastPdfReport />}
                      eyeReport={eyeReport}
                      duration={duration}
                      comments={comments}
                      needBreak={needBreak}
                    />
                    <SectionWrapper
                      report={examReport2}
                      sectionId={sectionId2}
                      childComponent={<ContrastPdfReport />}
                      eyeReport={eyeReport2}
                      duration={duration2}
                      comments={comments2}
                    />
                  </Grid>
                  {displayCanvas && <canvas width={1920}></canvas>}
                </div>
              );
            case examCardEnums.VISUAL_ACUITY.sectionName:
              return (
                <div key={`${sectionName}-${index}`} style={{ width: "100%" }}>
                  <Grid
                    container
                    className={sectionClassName}
                    columnSpacing={4}
                  >
                    <SectionWrapper
                      report={examReport}
                      sectionId={sectionId}
                      childComponent={<VisualAcuityPdfReport />}
                      eyeReport={eyeReport}
                      duration={duration}
                      comments={comments}
                      needBreak={needBreak}
                    />
                    <SectionWrapper
                      report={examReport2}
                      sectionId={sectionId2}
                      childComponent={<VisualAcuityPdfReport />}
                      eyeReport={eyeReport2}
                      duration={duration2}
                      comments={comments2}
                    />
                  </Grid>
                  {displayCanvas && <canvas width={1920}></canvas>}
                </div>
              );
            case examCardEnums.SYNOPTOPHORE.sectionName:
              return (
                <Grid
                  container
                  key={`${sectionName}-${index}`}
                  className={sectionClassName}
                  columnSpacing={4}
                >
                  <SectionWrapper
                    report={examReport}
                    sectionId={sectionId}
                    childComponent={<SynoptophoreReport />}
                    eyeReport={eyeReport}
                    durationStartTime={durationStartTime}
                    duration={duration}
                    comments={comments}
                  />
                </Grid>
              );
            case examCardEnums.VISUAL_FIELD_ESTERMAN.sectionName:
              return (
                <Grid
                  container
                  key={`${sectionName}-${index}`}
                  className={sectionClassName}
                  columnSpacing={4}
                >
                  <SectionWrapper
                    report={examReport}
                    sectionId={sectionId}
                    childComponent={<EstermanPdfReport />}
                    eyeReport={eyeReport}
                    durationStartTime={durationStartTime}
                    duration={duration}
                    comments={comments}
                  />
                </Grid>
              );
            case examCardEnums.VISUAL_FIELD_FULL_120.sectionName:
              return (
                <div key={`${sectionName}-${index}`} style={{ width: "100%" }}>
                  <Grid
                    container
                    className={sectionClassName}
                    columnSpacing={4}
                  >
                    <SectionWrapper
                      report={examReport}
                      sectionId={sectionId}
                      childComponent={<Full120Report />}
                      eyeReport={eyeReport}
                      durationStartTime={durationStartTime}
                      duration={duration}
                      comments={comments}
                      needBreak={needBreak}
                    />
                    <SectionWrapper
                      report={examReport2}
                      sectionId={sectionId2}
                      childComponent={<Full120Report />}
                      eyeReport={eyeReport2}
                      durationStartTime={durationStartTime2}
                      duration={duration2}
                      comments={comments2}
                    />
                  </Grid>
                </div>
              );
            case examCardEnums.VISUAL_FIELD_SCREENING.sectionName:
            case examCardEnums.VISUAL_FIELD_THRESHOLDING.sectionName:
              return (
                <div key={`${sectionName}-${index}`} style={{ width: "100%" }}>
                  <Grid
                    container
                    className={sectionClassName}
                    columnSpacing={4}
                  >
                    <SectionWrapper
                      report={examReport}
                      sectionId={sectionId}
                      childComponent={<VisualFieldPdfReport />}
                      eyeReport={eyeReport}
                      durationStartTime={durationStartTime}
                      duration={duration}
                      comments={comments}
                      needBreak={needBreak}
                    />
                    <SectionWrapper
                      report={examReport2}
                      sectionId={sectionId2}
                      childComponent={<VisualFieldPdfReport />}
                      eyeReport={eyeReport2}
                      durationStartTime={durationStartTime2}
                      duration={duration2}
                      comments={comments2}
                    />
                  </Grid>
                </div>
              );
            default:
              return <div key={index} style={{ width: "100%" }}></div>;
          }
        })}
      </>
    );
  }, [
    isMultiExamPackage,
    currentExamSections,
    exam,
    reportData,
    clinicSettings?.monocularReportPdfLayoutOption,
  ]);

  if (!shouldDisplayReport) {
    return <ErrorMessageComponent />;
  }

  return (
    <ReportDataContext.Provider value={{ ...reportPdfValues }}>
      <Grid container sx={{ display: "flex" }}>
        {isMultiExamPackage && <MultiExamReportPanel exam={exam} />}
        {reportSection}
      </Grid>
    </ReportDataContext.Provider>
  );
};

export default RegularExamPDF;
